import { BalanceSheetTable_BalanceSheetColumnFragment } from './graphql/BalanceSheetTable.fragments.generated';

export const DISPLAY_NAME_COLUMN_ID = 'name';
export const TOTAL_COLUMN_SUFFIX = '_TOTAL';
export const IN_ESTATE_TOTAL_COLUMN_ID = 'IN_ESTATE_TOTAL';
export const OUT_OF_ESTATE_TOTAL_COLUMN_ID = 'OUT_OF_ESTATE_TOTAL';
export const GRAND_TOTAL_COLUMN_ID = 'GRAND_TOTAL';
/** Total column ID used when both in-estate and out-of-estate are hidden (combined mode). */
export const ESTATE_TOTAL_COLUMN_ID = 'ESTATE_TOTAL';
/** The entity report has one value column, without estate or ownership groups. */
export const ENTITY_VALUE_COLUMN_ID = 'ENTITY_VALUE';

export function getIsTotalColumn(
  column: BalanceSheetTable_BalanceSheetColumnFragment
) {
  return column.ID.endsWith(TOTAL_COLUMN_SUFFIX);
}

/**
 * Returns the group ID that contains the given total column, or null if not found.
 */
function getGroupIdForTotalColumn(
  columns: BalanceSheetTable_BalanceSheetColumnFragment[],
  totalColumnId: string
): string | null {
  return columns.find((col) => col.ID === totalColumnId)?.group ?? null;
}

/**
 * Filters columns based on the current viewConfiguration show/hide toggles
 * and per-column visibility. This enables live preview in the table when the
 * user changes toggles or column checkboxes in the config panel before saving.
 *
 * Columns with isHidden=true are filtered out, and a draft override set allows
 * live preview of in-flight visibility changes. Total columns are never hidden.
 */
export function getViewConfigFilteredColumns(
  columns: BalanceSheetTable_BalanceSheetColumnFragment[],
  viewConfiguration: { showInEstate: boolean; showOutOfEstate: boolean } | null,
  options?: {
    draftHiddenColumns?: Set<string> | null;
  }
): BalanceSheetTable_BalanceSheetColumnFragment[] {
  const { draftHiddenColumns } = options ?? {};

  if (!viewConfiguration) return columns;

  const inEstateGroupId = getGroupIdForTotalColumn(
    columns,
    IN_ESTATE_TOTAL_COLUMN_ID
  );
  const outOfEstateGroupId = getGroupIdForTotalColumn(
    columns,
    OUT_OF_ESTATE_TOTAL_COLUMN_ID
  );

  return columns.filter((column) => {
    const isTotalColumn = getIsTotalColumn(column);

    if (
      column.ID === GRAND_TOTAL_COLUMN_ID &&
      (!viewConfiguration.showInEstate || !viewConfiguration.showOutOfEstate)
    ) {
      return false;
    }

    // Per-column visibility filtering. If the config panel has a draft set,
    // use that for live preview; otherwise fall back to the server's isHidden flag.
    if (!isTotalColumn) {
      if (draftHiddenColumns) {
        if (draftHiddenColumns.has(column.ID)) return false;
      } else if (column.isHidden) {
        return false;
      }
    }

    if (!viewConfiguration.showInEstate && column.group === inEstateGroupId) {
      return false;
    }
    if (
      !viewConfiguration.showOutOfEstate &&
      column.group === outOfEstateGroupId
    ) {
      return false;
    }
    return true;
  });
}
